/*
 * draw-traversal.ts - recursively select and render terrain tiles
 */

import type Map from './map';
import type MapSurfaceTree from './surface-tree';
import type MapSurfaceTile from './surface-tile';
import type MapMetanode from './metanode';
import type DrawTraversalMaskPool from './draw-traversal-mask';
import { TileRenderRig } from './tile-render-rig';
import type GpuTexture from '../renderer/gpu/texture';
import * as preV6Watertight from './pre-v6-watertight';


/**
 * Runs the multi-surface RFC draw-traversal for terrain.
 *
 * Each plain surface in `plainTrees` owns a private single-surface
 * helper tree (see `Map.resolvePlainSurfaceTrees`). The descent walks
 * those trees in lockstep over one combined sequence of tile positions:
 * at each `(lod, x, y)` it asks every still-active surface what it
 * knows about the position, asks the pass's descent whether it wants
 * a finer LOD from at least one of them, and recurses into quadrants
 * only the active set can still contribute to.
 *
 * On backtrack each surface that is at its natural leaf at the current
 * node draws against the accumulated mask; surfaces that still have
 * finer children render too, supplying fallback coverage. Partial
 * coverage propagates back to the parent through `maskPool.appendChild`;
 * watertight coverage propagates as state and exact quadrant
 * rectangles. Surfaces render front-to-back (last index first) so the
 * front surface claims pixels before the back ones can.
 *
 * Glues and virtual surfaces are never consulted.
 *
 * @param map Typed `Map` owning the frame.
 * @param plainTrees Per-plain-surface helper trees, ordered
 *     back-to-front (front surface at the last index).
 * @param maskPool Mask pool owned by the typed `Map`.
 * @param pass Sink, descent and pass-owned state for this traversal.
 */

export function drawTerrainTraversal(
    map: Map,
    plainTrees: MapSurfaceTree[],
    maskPool: DrawTraversalMaskPool,
    pass: TerrainTraversalPass,
): void {

    if (plainTrees.length === 0) return;

    const legacyMap = plainTrees[0].map;
    const draw = legacyMap.draw;
    const renderer = legacyMap.renderer;
    const entryTarget = renderer.gpu.currentRenderTarget;
    const descent = pass.descent;

    // Root rule (RFC 9): every configured terrain surface is a candidate
    // at the root. A root whose metanode is not ready is pending, not
    // absent; isMetanodeReady requests its load and keeps the map dirty, so
    // the traversal retries when the metanode arrives. The whole LOD-0
    // decision waits until every root is classified, so partial root
    // arrival cannot reorder surface priority. The order in `plainTrees`
    // is back-to-front; we preserve it in the active set and reverse on
    // render.
    const rootActive: ActiveSurface[] = [];

    for (const tree of plainTrees) {

        const tile = tree.surfaceTree;

        // A pending root leaves the frame undecided rather than letting the
        // traversal run on a partial root set.
        if (!tile.isMetanodeReady(tree, 0) || !tile.metanode) return;

        // Classified root: a culled one is left out of the active set,
        // the others join it.
        if (descent.culled(tile, tile.metanode)) continue;

        tile.updateTexelSize();
        rootActive.push({ tree, tile });
    }

    if (rootActive.length === 0) return;

    // The draw generation belongs to the colour frame; auxiliary passes
    // leave it and the tile stamps that follow it alone.
    if (pass.accounting) pass.accounting.drawCounter = ++draw.drawCounter;

    traverseNode({
        map,
        pass,
        active: rootActive,
        depth: 0,
        maskPool,
    });

    // Materializing a coverage mask leaves its own target bound.
    renderer.gpu.setRenderTarget(entryTarget);
}


/** State one terrain traversal pass owns, in place of global draw state. */
export type TerrainTraversalPass = {

    /** Produces this pass's output from the selected tiles. */
    sink: TerrainTraversalSink;

    /** Decides which tiles this pass descends into and may load. */
    descent: TerrainDescent;

    /** Present on the colour frame only. */
    accounting?: TerrainTraversalAccounting;
};


/**
 * What a pass wants from the tiles `drawTerrainTraversal` visits. The
 * traversal asks four questions and acts on the answers as follows.
 *
 * `culled`: the pass does not care about this tile. The tile is
 * dropped, the traversal does not go below it, and it is not drawn. Normally,
 * it also means the pass does not care about the area covered by this
 * tile. If every loaded child in a quadrant is culled, the quadrant counts as
 * off screen and no ancestor needs to cover it. An exception to this is when
 * the pass answers true to the fourth question (see `wantsCovered` below).
 *
 * `wantsFiner`: the pass wants to descend in the tile hierarchy to obtain more
 * detail than this tile has.
 *
 * `fallbackFetches`: a fallback tile is a tile for which a pass wants
 * finer detail. The tree traversal draws them if they are ready to draw.
 * This pass method tells whether this specific fallback tile should fetch its
 * resources. Metanodes are not the descent's business: the traversal requests
 * the metanode of every tile it reaches.
 *
 * `wantsCovered`: a specific exception to the 'off-screen' rule. For a culled
 * tile, this indicates the pass wants the area of this culled tile covered
 * by its ancestors. Color and depth passes always return false, but the
 * elevation pass returns true under very specific circumstances - basically
 * when the parent tile needs to draw to provide samples for terrain probes.
 */
export type TerrainDescent = {

    /** Whether the pass does not care about this tile. */
    culled(tile: MapSurfaceTile, node: MapMetanode): boolean;

    /** Whether the pass wants the area of a culled tile covered by an
     * ancestor's draw. Asked for culled tiles only. */
    wantsCovered(tile: MapSurfaceTile, node: MapMetanode): boolean;

    /** Whether the pass wants more detail than this tile has. */
    wantsFiner(tile: MapSurfaceTile, node: MapMetanode): boolean;

    /** Whether a fallback draw of this tile fetches its resources. */
    fallbackFetches(tile: MapSurfaceTile): boolean;
};


/**
 * Produces one kind of output from the tiles `drawTerrainTraversal`
 * selects. It owns its render target, its program, and any effects of
 * its own; the pass's descent decides which tiles it culls, wants finer
 * and may load, and terrain-source order, fallback selection, coverage
 * masks, and watertightness stay in the traversal.
 */
export type TerrainTraversalSink = {

    /**
     * Opens a node on backtrack: its children have completed, nothing
     * has been drawn at it yet, and every active surface shares
     * `tileId`.
     */
    beginNode?(tileId: [number, number, number]): void;

    /**
     * Whether a rig can produce this sink's output, making its
     * resources ready when `options` permits loading. Called for the
     * current rig, then for the tile's last rig at fallback readiness.
     */
    isReady(
        rig: TileRenderRig,
        readiness: TileRenderRig.ReadinessLevels,
        priority: TileRenderRig.Priority,
        options: TileRenderRig.IsReadyOptions,
    ): boolean;

    /**
     * Draws one ready rig. The bound GPU target is not the sink's, so
     * it binds its own.
     *
     * @param maskTexture coverage from finer descendants and
     *     higher-priority surfaces, absent when the node has none
     */
    draw(
        tile: MapSurfaceTile,
        rig: TileRenderRig,
        maskTexture?: GpuTexture,
    ): void;

    /**
     * Closes a node opened by `beginNode`, exactly once, on whichever
     * path leaves it.
     *
     * @param covered whether the node ended up covered, by its own
     *     draws or by its children
     */
    endNode?(
        tileId: [number, number, number],
        covered: boolean,
        watertight: boolean,
    ): void;
};


/**
 * Colour-frame accounting the traversal fills in. The colour caller
 * allocates it and reads the counters into `MapStats` afterwards;
 * auxiliary passes leave it out and touch none of it.
 */
export type TerrainTraversalAccounting = {

    /** Draw generation this pass stamps on the tiles it draws. */
    drawCounter: number;

    usedNodes: number;
    processedNodes: number;
    processedMetatiles: number;
};


/**
 * Recurses into one tile position and returns the coverage produced by
 * the subtree.
 *
 * `active` carries the surfaces that are still candidates at this
 * node: each one has a ready, visible metanode at this `(lod, x, y)`.
 * Surfaces drop out of the active set as they fail visibility, lose
 * their metanode, or reach a position their tree no longer has a
 * child for.
 *
 * The returned kind tells the caller how this quadrant of the parent is
 * covered:
 *   - `watertight`: the on-screen cell is fully covered; the caller
 *     records it analytically (a quadrant rectangle), no mask.
 *   - `partial`: covered with an arbitrary shape that lives in this
 *     depth's node mask; the caller blits it into the parent.
 *   - `gap`: on-screen but nothing rendered yet (waiting for data); the
 *     caller leaves it uncovered for its own draw or an ancestor to fill.
 *   - `empty`: no on-screen area at all (off-screen); the caller folds it
 *     into a watertight result instead of drawing a fallback.
 *
 * @param context Frame-wide state plus the active surface set and the
 *     current recursion depth.
 * @returns Coverage state for the subtree rooted at this node.
 */
function traverseNode(context: NodeContext): NodeCoverageResult {

    const {
        active,
        depth,
        maskPool,
    } = context;

    // this should not happen, but just in case
    if (active.length === 0) return 'empty';

    // The bbox-visibility check has already been applied to every
    // entry of `active` by the caller (root setup or the child loop
    // below); we only record stats here. The depth-local mask is
    // cleared lazily once partial coverage first needs storage.
    recordSurfaces(context);

    // Combined descent decision: any active surface that still has a
    // child and that the pass wants finer forces descent. We descend
    // into quadrants whose child set is non-empty.
    const descent = context.pass.descent;
    let shouldDescend = false;

    // is there a fitting surface with watertight coverage? This
    // always stops descent, even if the surface is not front-most
    // we trust the claims surfaces make about their level of detail and
    // coverage, this also prevents force decent to surface that have geometry
    // available only on finer LODs. A surface fits when the pass does
    // not want it finer.
    let hasWatertightFit = false;

    // the pass's answer per surface, kept for the backtrack draw
    const wantsFiner: boolean[] = [];

    for (const entry of active) {

        const node = entry.tile.metanode!;
        const finer = descent.wantsFiner(entry.tile, node);

        wantsFiner.push(finer);
        if (node.hasChildren() && finer) shouldDescend = true;
        if (node.watertight && !finer) hasWatertightFit = true;
    }

    // Process children if applicable. We descend if an active surface can
    // benefit from finer LOD, but not if we already have a watertight fit
    // from any active surface

    // Each node starts from empty coverage. Reset is CPU-cheap (it clears
    // a rectangle list), so there is no lazy-init guard.
    maskPool.resetCoverage(depth);

    let watertightMask = 0;
    let offScreenMask = 0;

    if (!hasWatertightFit && shouldDescend) {

        for (let quadrant = 0; quadrant < 4; quadrant++) {

            const child = collectChildActive(context, quadrant);

            // Metadata-first (RFC 9): a quadrant with any pending candidate
            // is not recursed this frame. Skipping it without marking it
            // empty or watertight leaves it an unresolved gap, so this node
            // renders its natural leaves or fallback while the child
            // metanode loads. collectChildActive already requested that
            // load through getReadyChild.
            if (child.pending) continue;

            if (child.active.length === 0) {

                // At least one loaded child was off-screen and every candidate
                // was absent or culled. Nothing finer needs coverage here, so
                // fold the quadrant in. With only absent children there is
                // no visibility evidence; leave a gap for parent fallback.
                if (child.culled)
                    offScreenMask |= 1 << quadrant;
                continue;
            }

            const childContext: NodeContext = {
                ...context,
                active: child.active,
                depth: depth + 1,
            };

            // recurse into the child quadrant
            const childCoverage = traverseNode(childContext);

            // classify coverage
            if (childCoverage === 'watertight') {

                watertightMask |= 1 << quadrant;
                continue;
            }

            if (childCoverage === 'off-screen') {

                offScreenMask |= 1 << quadrant;
                continue;
            }

            if (childCoverage === 'partial') {

                // Fold the child's mask into this node's quadrant: its
                // rectangles map up on the CPU, footprint coverage blits
                // up only if the child has any.
                maskPool.appendChild(depth + 1, depth, quadrant);
                continue;
            }

            // end quadrant processing
        }
    }

    for (const entry of active) {
        preV6Watertight.inferPreV6WatertightFromChildren(entry.tile);
    }

    // Backtracking starts here. The sink sees the node before every path
    // that can leave it, including the two returns below, and sees
    // exactly one endNode on whichever path is taken.
    const sink = context.pass.sink;
    const tileId = active[0].tile.id;

    sink.beginNode?.(tileId);

    // No on-screen area anywhere below: this node contributes nothing.
    if (offScreenMask === AllQuadrantsMask) {

        sink.endNode?.(tileId, false, false);
        return 'off-screen';
    }

    // Every on-screen quadrant is watertight (the rest are off-screen):
    // covered, no draw or mask needed, early return
    if ((watertightMask | offScreenMask) === AllQuadrantsMask) {

        sink.endNode?.(tileId, true, true);
        return 'watertight';
    }

    // Record watertight children as exact quadrant rectangles.
    if (watertightMask !== 0) maskPool.addQuadrantRects(depth, watertightMask);

    // Render surfaces front-to-back. The last entry is the front surface
    // (lowest `viewSurfaceIndex`), so iterate in reverse.
    //
    // There are three backtrack render cases:
    //
    // 1. Natural leaf: render unconditionally with full desired readiness.
    // 2. Fetching fallback: render a non-leaf fallback LOD the pass fetches
    //    for and allow missing fallback resources to be requested.
    // 3. Probe: try a non-leaf fallback draw with loading disabled. This
    //    keeps an already available intermediate LOD visible while the
    //    deeper natural leaf loads, without making every traversed LOD a
    //    proactive fallback request.

    // we bailout from rendering on the first surface that does not render
    // to prevent transient loading artifacts
    let noRender = false;

    // iterate through surfaces
    for (let i = active.length - 1; i >= 0; i--) {

        const entry = active[i];
        const node = entry.tile.metanode!;
        const naturalLeaf = !(node.hasChildren() && wantsFiner[i]);

        const readiness = naturalLeaf ? ReadinessFull : ReadinessFallback;

        // Load only natural leaves, fetching fallbacks, and the surfaces
        // that are in front of a watertight-fit stop. The last is the least
        // obvious: it saves a non-fitting front surface at a node where a
        // watertight back surface stops descent.
        const preventLoad = !naturalLeaf && !hasWatertightFit
            && !descent.fallbackFetches(entry.tile);

        // render, using anything resident. Fetches are requested only when
        // preventLoad is false.
        const renderedCoverage =
            renderTile(
                context,
                entry,
                readiness,
                noRender,
                preventLoad,
            );

        // A surface drawing watertight fully covers the node.
        if (renderedCoverage === 'watertight') {

            sink.endNode?.(tileId, true, true);
            return 'watertight';
        }

        // front surface failed to render; skip rendering the rest
        if (renderedCoverage === 'loading') noRender = true;

        // A watertight metanode claims the node even before it draws, so
        // stop fetching and rendering lower-priority surfaces under it.
        if (node.watertight) break;
    }

    // The node's coverage is whatever ended up in its mask: watertight
    // rectangles, blitted child masks, or rendered footprints.
    const covered = maskPool.hasCoverage(depth);

    sink.endNode?.(tileId, covered, false);
    return covered ? 'partial' : 'empty';
}


/**
 * Classifies the active surface set against one child quadrant of the
 * current node (RFC 9 metadata-first). Each candidate surface lands in
 * one of four outcomes for this quadrant:
 *   - absent: the parent metanode proves the surface has no child here;
 *   - pending: the parent has the child but its metanode is not ready;
 *   - culled: the child metanode is ready and the pass culls the child;
 *   - ready: the child metanode is ready and the pass keeps the child,
 *     so it joins the child active set.
 *
 * `pending` is set on the quadrant when any candidate is pending. The
 * caller does not recurse a pending quadrant: a not-ready candidate blocks
 * descent for the frame so a slower surface cannot be skipped over. Its
 * load is requested through `getReadyChild`.
 *
 * `culled` is the final quadrant classification. It is true only when there
 * are no ready children, no child metadata is pending, at least one loaded
 * child was rejected as culled, and no culled child wants its area covered.
 * Every surface's child is therefore either absent or culled. When every
 * child is absent, or a culled child wants its area covered, `culled`
 * remains false and the caller leaves a gap for parent fallback.
 */
function collectChildActive(
    context: NodeContext,
    quadrant: number,
): ChildQuadrant {

    const { active } = context;
    const descent = context.pass.descent;
    const childActive: ActiveSurface[] = [];

    let sawCulledChild = false;
    let wantsCovered = false;

    // Set when a candidate has a child here whose metanode has not loaded
    // yet. One pending candidate blocks recursion into the quadrant.
    let pending = false;

    for (const entry of active) {

        const node = entry.tile.metanode!;

        if (!node.hasChild(quadrant)) {
            continue;            // absent: surface has no child here
        }

        const childTile = getReadyChild(entry.tree, entry.tile, quadrant);

        if (!childTile || !childTile.metanode) {

            pending = true;      // child exists but its metanode is not ready
            continue;
        }

        if (descent.culled(childTile, childTile.metanode)) {

            sawCulledChild = true;
            if (descent.wantsCovered(childTile, childTile.metanode))
                wantsCovered = true;
            continue;            // finer child loaded and culled
        }

        childTile.updateTexelSize();
        childActive.push({ tree: entry.tree, tile: childTile });
    }

    const culled = !pending
        && childActive.length === 0
        && sawCulledChild
        && !wantsCovered;

    return { active: childActive, culled, pending };
}


/**
 * Draws one surface at the current node using the depth-local node
 * mask as the read-and-write coverage. Only a tile that actually draws
 * can produce watertight coverage: a drawn watertight tile returns
 * analytic coverage instead of rasterizing its footprint into the mask.
 */
function renderTile(
    context: NodeContext,
    entry: ActiveSurface,
    readiness: TileRenderRig.ReadinessLevels,
    noRender = false,
    preventLoad = false,
): TileRenderResult {

    const { tree, tile } = entry;
    const { depth, map, maskPool, pass } = context;
    const legacyMap = tree.map;
    const node = tile.metanode;

    // sanity
    if (!node) return 'loading';
    if (!tile.surface) return 'partial';

    // structural node -> bail out
    if (!node.hasGeometry()) return 'structural';

    // the old vts priority formula
    const priority = tile.id[0] * tile.distance;

    // escape hatch for no-load probing
    if (preventLoad && !tile.surfaceMesh) return 'loading';

    // check gpu budget
    if (legacyMap.stats.gpuRenderUsed >= legacyMap.draw.maxGpuUsed)
        return 'loading';

    // create mesh object if it does not exist yet
    if (!tile.surfaceMesh) {
        let path = tile.resourceSurface.getMeshUrl(tile.id);
        tile.surfaceMesh = tile.resources.getMesh(path, tile);
    }

    // create a new rig when we are either drawing the tile for the first
    // time, or there has been a raster fallback, or layer visibility has
    // mutated (view switched under legacy terminology)
    if (!tile.tileRenderRig || tile.updateBounds || tile.resetDrawCommands) {

        if (tile.lastRenderRig) tile.lastRenderRig.dispose();
        if (tile.tileRenderRig) tile.lastRenderRig = tile.tileRenderRig;

        // create new rig from submeshSurface layer sequence
        tile.tileRenderRig = new TileRenderRig(
            tile, map.style!.style(), map.renderer, map.config);

        // rig was rebuilt -> clear update flag
        tile.updateBounds = tile.resetDrawCommands = false;
    }

    // no render -> bail out
    if (noRender) return 'no-render';

    // do we have a rig ready to draw?
    let curRig = tile.tileRenderRig;
    let lastRig = tile.lastRenderRig;
    let curRigReady = false;

    let readyOptions: TileRenderRig.IsReadyOptions = {
        doNotLoad: preventLoad,
        doNotCheckGpu: false,
    };

    let priority_: TileRenderRig.Priority = {
        essential: priority,
        optional: priority,
    };

    const sink = pass.sink;

    curRigReady = sink.isReady(curRig, readiness, priority_, readyOptions);

    let lastRigReady = false;

    if (!curRigReady && lastRig)
        lastRigReady = sink.isReady(lastRig,
            TileRenderRig.ReadinessFallback, priority_, readyOptions);

    let rigToDraw : TileRenderRig | null = curRigReady ? curRig :
        lastRigReady ? lastRig : null;

    // no rig, nothing to draw -> bail out
    if (!rigToDraw) return 'loading';

    // malformed terrain data -> bail out
    if (!rigToDraw.hasGeometry()) return 'no-render';

    // Sample prior coverage (finer descendants and higher-priority
    // surfaces drawn before this one) only when some exists; materialize
    // rasterizes the rectangle list and footprint texture on demand.
    const erosion = map.config.mapTraversalMaskErosion;
    const maskTexture = maskPool.hasCoverage(depth)
        ? maskPool.materialize(depth, erosion)
        : undefined;

    sink.draw(tile, rigToDraw, maskTexture);

    // update draw generation counter, infer watertightness if applicable
    if (pass.accounting) tile.drawCounter = pass.accounting.drawCounter;
    preV6Watertight.inferPreV6WatertightFromTile(tile);

    // done, drawn and watertight
    if (node.watertight) return 'watertight';

    // done, drawn and partial, add footprint for back surfaces and fallbacks
    maskPool.addFootprint(rigToDraw!, depth);
    return 'partial';
}


/**
 * Returns the child tile at `quadrant` if it is allocated and its
 * metanode for this position is ready; otherwise null. A missing
 * metanode is requested.
 */
function getReadyChild(
    tree: MapSurfaceTree,
    tile: MapSurfaceTile,
    quadrant: number,
): MapSurfaceTile | null {

    const child = tile.children[quadrant];
    if (!child) return null;

    if (!child.isMetanodeReady(tree, child.id[0])) return null;
    if (!child.metanode) return null;

    return child;
}


/**
 * Counts each active surface's node and the first appearance of each
 * metatile this frame. The first-appearance check is per-metatile, not
 * per-surface, because the same metatile binary backs all nodes in a
 * metatile-aligned block.
 *
 * Only the colour frame accounts for nodes and metatiles, so an
 * auxiliary pass leaves the metatile generation stamps alone too.
 */
function recordSurfaces(context: NodeContext): void {

    const accounting = context.pass.accounting;
    if (!accounting) return;

    const drawCounter = accounting.drawCounter;

    for (const entry of context.active) {

        accounting.processedNodes++;
        accounting.usedNodes++;

        const metatile = entry.tile.metanode!.metatile;
        if (metatile.drawCounter === drawCounter) continue;

        metatile.drawCounter = drawCounter;
        accounting.processedMetatiles++;
    }
}


/** One participating surface at a tile position during traversal. */
type ActiveSurface = {
    tree: MapSurfaceTree;
    tile: MapSurfaceTile;
};


/**
 * One quadrant's classified children: the ready set, final culling
 * classification, and whether child metadata is still pending.
 */
type ChildQuadrant = {
    active: ActiveSurface[];
    culled: boolean;
    pending: boolean;
};


type NodeContext = {
    map: Map;
    pass: TerrainTraversalPass;
    active: ActiveSurface[];
    depth: number;
    maskPool: DrawTraversalMaskPool;
};

/* results of rendering a single surface at a a given node - a single tile.
 */
type TileRenderResult =
    | 'watertight'      // fully covered
    | 'partial'         // covered with an arbitrary shape, in a mask
    | 'loading'         // not rendered, waiting for data
    | 'structural'      // early exit, no geometry here to render or fetch
    | 'no-render'           // no-render or malformed data

/** node coverage results - the state of coverage after processing
 *  the entire subtree. These effect optimization during backtracking
 */
type NodeCoverageResult =
    | 'watertight'      // fully covered
    | 'partial'         // covered with an arbitrary shape, in a mask
    | 'empty'           // no content rendered (yet)
    | 'off-screen';     // no on-sceen area (completely culled)

const AllQuadrantsMask = 0b1111;

const ReadinessFull: TileRenderRig.ReadinessLevels = {
    minimum: 'fallback',
    desired: 'full',
};

const ReadinessFallback: TileRenderRig.ReadinessLevels = {
    minimum: 'fallback',
    desired: 'fallback',
};
